import React, { useState } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Check, X, Trash2 } from "lucide-react";
import ClubBasicFields from "./ClubBasicFields";
import ClubTeamsAndColors from "./ClubTeamsAndColors";
import ClubPresidentFields from "./ClubPresidentFields";

const STATUS_CLS = {
    approved: "border-green-700 text-green-400",
    pending: "border-orange-500/40 text-orange-500",
    rejected: "border-red-900 text-red-400",
};

export function EditClubPanel({ club, onClose }) {
    const [f, setF] = useState({
        name: club.name || "",
        sport: club.sport || "Football",
        department: club.department || "",
        city: club.city || "",
        teams_by_category: club.teams_by_category || [],
        level: club.level || "Loisir",
        colors: club.colors || [],
        needs: club.needs || "",
        description: club.description || "",
        president_name: club.president_name || "",
        president_phone: club.president_phone || "",
    });
    const [saving, setSaving] = useState(false);

    const setStatus = async (status) => {
        try {
            await api.post(`/admin/clubs/${club.id}/status?status=${status}`);
            toast.success("Statut mis à jour");
            onClose();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...f, age_categories: f.teams_by_category.map((t) => t.age_category) };
            await api.put(`/clubs/${club.id}`, payload);
            toast.success("Club mis à jour");
            onClose();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const remove = async () => {
        if (!window.confirm(`Supprimer ${club.name} ? Cette action est irréversible.`)) return;
        try {
            await api.delete(`/admin/clubs/${club.id}`);
            toast.success("Club supprimé");
            onClose();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <button
                        onClick={onClose}
                        className="text-xs uppercase tracking-widest text-zinc-400 hover:text-orange-500 mb-2"
                    >
                        ← Retour
                    </button>
                    <h2 className="font-display text-4xl uppercase tracking-tight">{club.name}</h2>
                    <span className={`chip mt-2 ${STATUS_CLS[club.status] || STATUS_CLS.rejected}`}>{club.status}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                    {club.status !== "approved" && (
                        <button
                            onClick={() => setStatus("approved")}
                            data-testid="panel-approve"
                            className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1"
                        >
                            <Check size={14} /> Approuver
                        </button>
                    )}
                    {club.status !== "rejected" && (
                        <button
                            onClick={() => setStatus("rejected")}
                            data-testid="panel-reject"
                            className="px-4 py-2 border border-red-900 hover:bg-red-950 text-red-400 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1"
                        >
                            <X size={14} /> Rejeter
                        </button>
                    )}
                    <button
                        onClick={remove}
                        data-testid="panel-delete"
                        className="px-4 py-2 border border-zinc-700 hover:border-red-500 hover:text-red-500 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1"
                    >
                        <Trash2 size={14} /> Supprimer
                    </button>
                </div>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); save(); }} className="card-tu space-y-5">
                <ClubBasicFields f={f} setF={setF} />
                <ClubTeamsAndColors f={f} setF={setF} />
                <div>
                    <label className="label-tu">Besoins</label>
                    <input value={f.needs} onChange={(e) => setF({ ...f, needs: e.target.value })} className="input-tu" />
                </div>
                <div>
                    <label className="label-tu">Description</label>
                    <textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="input-tu" />
                </div>
                <ClubPresidentFields f={f} setF={setF} />
                <button type="submit" disabled={saving} data-testid="panel-save" className="btn-primary w-full">
                    {saving ? "Enregistrement…" : "Enregistrer les modifications"}
                </button>
            </form>
        </div>
    );
}
